import apiClient from "./apiClient";
import { comCache } from "./cacheGet";

export interface FiltroHome {
  operadoras: number[];
  cnpjs: string[];
  inicio: string;
  fim: string;
}

export type FiltroBase = Pick<FiltroHome, "operadoras" | "cnpjs">;

export type PeriodoHome = Pick<FiltroHome, "inicio" | "fim">;

export type TipoPainel = "VENDA" | "PAGAMENTO" | "ANTECIPADO";

export interface OperadoraFatia {
  name: string;
  y: number;
}

export type FormatoIndicador = "MOEDA" | "INTEIRO" | "PERCENTUAL";

export interface IndicadorPainel {
  titulo: string;
  valor: number;
  destaque: boolean;
  formato?: FormatoIndicador;
  descricao?: string;
}

export interface PainelResponse {
  operadoras: OperadoraFatia[];
  indicadores: IndicadorPainel[];
}

export interface IndicadorComparativo {
  valor: number;
  quantidade: number;
  variacao?: number | null;
}

export interface AjustesComparativo {
  creditos: number;
  debitos: number;
  liquido: number;
  variacao?: number | null;
}

export interface DiaComparativo {
  rotulo: string;
  vendaAtual: number;
  vendaAnterior: number;
  pagamento: number;
}

export interface DesempenhoOperadora {
  nome: string;
  valor: number;
  participacao: number;
}

export interface ComparativoResponse {
  vendas: IndicadorComparativo;
  pagamentos: IndicadorComparativo;
  antecipacoes: IndicadorComparativo;
  ajustes: AjustesComparativo;
  previsao: IndicadorComparativo;
  ticketMedio: IndicadorComparativo;
  dias: DiaComparativo[];
  operadoras: DesempenhoOperadora[];
  inicio?: string;
  fim?: string;
  inicioAnterior?: string;
  fimAnterior?: string;
}

export interface PrevisaoMes {
  mes?: string;
  rotulo: string;
  total: number;
  operadoras: OperadoraFatia[];
}

export interface PrevisaoResponse {
  mesCorrente: PrevisaoMes | null;
  totalPrevisto: number;
  meses: PrevisaoMes[];
}

export interface FiltroOperadora {
  codOperadora: number;
  nome: string;
}

export interface FiltroUnidade {
  codPessoa: number;
  apelido: string;
  documento: string;
}

export const listaOperadoras = (operadoras: number[] | null | undefined): number[] => {
  if (!operadoras) {
    return [];
  }
  return operadoras
    .filter((codigo, indice) => codigo != null && operadoras.indexOf(codigo) === indice)
    .sort((um, outro) => um - outro);
};

export const listaDocumentos = (documentos: string[] | null | undefined): string[] => {
  if (!documentos) {
    return [];
  }
  return documentos
    .filter((documento, indice) => Boolean(documento) && documentos.indexOf(documento) === indice)
    .sort();
};

const comFiltroOpcional = (filtro: FiltroBase, params: { [chave: string]: unknown }) => {
  const operadoras = listaOperadoras(filtro.operadoras);
  if (operadoras.length > 0) {
    params.operadora = operadoras.join(",");
  }
  const documentos = listaDocumentos(filtro.cnpjs);
  if (documentos.length > 0) {
    params.cnpj = documentos.join(",");
  }
  return params;
};

export const buscarPainel = async (tipo: TipoPainel, filtro: FiltroHome): Promise<PainelResponse> => {
  const params = comFiltroOpcional(filtro, { tipo, inicio: filtro.inicio, fim: filtro.fim });
  return comCache("/home/painel", params, async () => {
    const { data } = await apiClient.get<PainelResponse>("/home/painel", { params });
    return data;
  });
};

export const buscarComparativo = async (filtro: FiltroBase, semana: string): Promise<ComparativoResponse> => {
  const params = comFiltroOpcional(filtro, { inicio: semana });
  return comCache("/home/comparativo", params, async () => {
    const { data } = await apiClient.get<ComparativoResponse>("/home/comparativo", { params });
    return data;
  });
};

export const buscarPrevisoes = async (filtro: FiltroHome): Promise<PrevisaoResponse> => {
  const params = comFiltroOpcional(filtro, { fim: filtro.fim });
  return comCache("/home/previsoes", params, async () => {
    const { data } = await apiClient.get<PrevisaoResponse>("/home/previsoes", { params });
    return data;
  });
};

export interface TransacaoDetalhe {
  codLinha: number;
  dataHora: string;
  nsu: string;
  tid: string;
  operadora: string;
  bandeira: string;
  modalidade: string;
  autorizacao: string;
  parcelas: string;
  valorBruto: number;
  valorLiquido: number;
  taxaValor: number;
  taxaPercentual: number;
  status: string;
  ro?: string;
  diasAntecipados?: number;
  taxaAntecipacao?: number;
  taxaTotal?: number;
  codPessoa?: number | null;
  origem?: string;
}

export interface ResumoTransacoes {
  totalTransacoes: number;
  valorBruto: number;
  valorLiquido: number;
  cancelamentos: number;
  mediaTaxaPorTransacao: number;
  aprovadas: number;
  canceladas: number;
  valorCliente: number | null;
  taxaCliente: number;
  valorOperadora: number;
  taxaOperadora: number;
  diferenca: number | null;
  conciliados: number;
  divergentes: number;
  pendentes: number;
  taxaMediaAntecipacao?: number;
  taxaMediaTotal?: number;
  diasMediosAntecipacao?: number;
  descontoTotal?: number;
}

export interface OpcoesTransacoes {
  operadoras: string[];
  bandeiras: string[];
  modalidades: string[];
}

export interface TransacoesResponse {
  resumo: ResumoTransacoes | null;
  transacoes: TransacaoDetalhe[];
  totalRegistros: number;
  pagina: number;
  tamanho: number;
  opcoes?: OpcoesTransacoes | null;
}

export interface Parcela {
  parcela: string;
  vencimento: string;
  dataLancamento: string;
  status: string;
  valorBruto: number;
  taxaPercentual: number;
  valorLiquido: number;
  origem?: string;
}

export interface ArquivoRelacionado {
  nome: string;
  recebimento: string;
}

export interface TransacaoRastreabilidade {
  nomeArquivo: string;
  origem: string;
  recebimento: string;
  nsu: string;
  autorizacao: string;
  ro: string;
  tid: string;
  dataHora: string;
  operadora: string;
  valorBruto: number;
  valorLiquido: number;
  status: string;
  arquivos: ArquivoRelacionado[];
  parcelas: Parcela[];
  parcela?: string;
  diasAntecipados?: number;
  taxaAntecipacao?: number;
  taxaMdr?: number;
  arquivosAntecipacao?: ArquivoRelacionado[];
}

export type TipoTransacao = "VENDA" | "PAGAMENTO" | "ANTECIPADO";

export interface FiltroTransacoes {
  pagina: number;
  tamanho: number;
  busca: string;
  operadora: string;
  bandeira: string;
  modalidade: string;
  status: string;
  incluir?: "resumo" | "lista" | "opcoes";
}

export const buscarTransacoes = async (
  tipo: TipoTransacao,
  filtro: FiltroHome,
  filtroTabela: FiltroTransacoes
): Promise<TransacoesResponse> => {
  const params = comFiltroOpcional(filtro, {
    tipo,
    inicio: filtro.inicio,
    fim: filtro.fim,
    pagina: filtroTabela.pagina,
    tamanho: filtroTabela.tamanho,
  });
  if (filtroTabela.busca) {
    params.busca = filtroTabela.busca;
  }
  if (filtroTabela.operadora) {
    params.nomeOperadora = filtroTabela.operadora;
  }
  if (filtroTabela.bandeira) {
    params.bandeira = filtroTabela.bandeira;
  }
  if (filtroTabela.modalidade) {
    params.modalidade = filtroTabela.modalidade;
  }
  if (filtroTabela.status) {
    params.status = filtroTabela.status;
  }
  if (filtroTabela.incluir) {
    params.incluir = filtroTabela.incluir;
  }
  return comCache("/home/transacoes", params, async () => {
    const { data } = await apiClient.get<TransacoesResponse>("/home/transacoes", { params });
    return data;
  });
};

export const buscarRastreabilidade = async (
  codLinha: number,
  tipo: TipoTransacao,
  origem?: string
): Promise<TransacaoRastreabilidade> => {
  const params: Record<string, string> = { tipo };
  if (origem) {
    params.origem = origem;
  }
  return comCache(`/home/transacoes/${codLinha}`, params, async () => {
    const { data } = await apiClient.get<TransacaoRastreabilidade>(`/home/transacoes/${codLinha}`, {
      params,
    });
    return data;
  });
};

export const listarOperadoras = async (): Promise<FiltroOperadora[]> => {
  return comCache("/filtros/operadoras", undefined, async () => {
    const { data } = await apiClient.get<FiltroOperadora[]>("/filtros/operadoras");
    return data;
  });
};

export const consultarLimitePeriodo = async (): Promise<number> => {
  return comCache("/filtros/periodo", undefined, async () => {
    const { data } = await apiClient.get<{ maxDias: number }>("/filtros/periodo");
    return data.maxDias;
  });
};

export const listarUnidades = async (): Promise<FiltroUnidade[]> => {
  return comCache("/filtros/unidades", undefined, async () => {
    const { data } = await apiClient.get<FiltroUnidade[]>("/filtros/unidades");
    const documentos: string[] = [];
    return data.filter((unidade) => {
      if (documentos.indexOf(unidade.documento) >= 0) {
        return false;
      }
      documentos.push(unidade.documento);
      return true;
    });
  });
};
